/**
 * Copyright (c) 2021 MeetOnline.IO
 *
 * long description for the file
 *
 * @summary UTIL.js having utility functions
 * @author Rishabh <rishabh.it.007@gmail.com>
 *
 * Created at     : 2021-02-26 12:00:00
 * Last modified  : 2021-02-26 12:00:00
 */
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { ChatModel } from "../recoil/atoms/chats/types"
import { v4 as uuidv4 } from "uuid"
import { getRandomIntInclusive } from "./getRandom"
import { UserModel } from "../recoil/atoms/users/types"
import { LinkElement } from "../types"
import { ReactionModalType } from "../recoil/atoms/chats/types"
import { waitForElm } from "./waitForElm"
import { ModNotPresentType } from "../constants/enums"
import MOLocation from "./MOLocation"
export const now = () => dayjs().format("yyyy-MM-dd HH:mm:ss")
export const UTCTime = () => {
  dayjs.extend(utc)
  return dayjs().utc().format("YYYY-MM-DD HH:mm:ss")
}
export const timeMilis = () => dayjs().unix()
export const currentTime = () => dayjs().format("HH:mm a")
export const currentDay = () => dayjs().format("dddd")
export const currentDate = () => dayjs().format("DD MMMM YYYY")
export const currentYear = () => dayjs().format("YYYY")

export const formatTime = (time: string) => dayjs(time).format("HH:mm a")
export const formatTime24h = (time: string) => {
  dayjs.extend(utc)
  return dayjs(time).utc(true).local().format("HH:mm")
}

const UserType = window.MeetOnline?.UserType

export const formatFullTime = (time: string) => {
  dayjs.extend(utc)
  return dayjs(time).utc(true).local().format("YYYY-MM-DD HH:mm:ss")
}

/**
 * Converting "2023-07-21 16:05:00" to "2023-07-21T16:05:00.000Z"
 */
export function convertDateStringToISO(inputDate: string) {
  // Split the input date string into date and time components
  const [datePart, timePart] = inputDate.split(" ")

  // Split the time component into hours, minutes, and seconds
  const [hours, minutes, seconds] = timePart.split(":")

  // Extract the individual date components (year, month, day)
  const [year, month, day] = datePart.split("-")

  // Create a Date object with UTC date and time components
  const utcDate = new Date(
    Date.UTC(+year, +month - 1, +day, +hours, +minutes, +seconds)
  )

  // Convert the Date object to UTC string (ISO 8601 format with "Z" indicating UTC)
  const utcDateString = utcDate.toISOString()

  return utcDateString
}

const compare = (a: any, b: any, key: string | number) => {
  if (a[key] < b[key]) {
    return -1
  }
  if (a[key] > b[key]) {
    return 1
  }
  return 0
}

export const userSorter = (u1: UserModel, u2: UserModel) => {
  if (u1.handUp === true && u2.handUp !== true) {
    return -1
  }
  if (u1.handUp !== true && u2.handUp === true) {
    return 1
  }

  if (u1.type === UserType?.MODERATOR && u1.type !== UserType?.MODERATOR) {
    return -1
  }
  if (u1.type !== UserType?.MODERATOR && u1.type === UserType?.MODERATOR) {
    return 1
  }
  if (u1.type === UserType?.MODERATOR && u1.type === UserType?.MODERATOR) {
    return compare(u1, u2, "name")
  }

  if (u1.type === UserType?.BROADCASTER && u1.type !== UserType?.BROADCASTER) {
    return -1
  }
  if (u1.type !== UserType?.BROADCASTER && u1.type === UserType?.BROADCASTER) {
    return 1
  }
  if (u1.type === UserType?.BROADCASTER && u1.type === UserType?.BROADCASTER) {
    return compare(u1, u2, "name")
  }

  return compare(u1, u2, "name")
}

export const previousSpeakerSorter = (p1: UserModel, p2: UserModel) => {
  if (p1.camEnabled === true && p2.camEnabled !== true) {
    return -1
  } else if (p1.camEnabled !== true && p2.camEnabled === true) {
    return 1
  } else if (
    (p1.camEnabled === true && p2.camEnabled === true) ||
    (p1.camEnabled !== true && p2.camEnabled !== true)
  ) {
    if (
      typeof p1.lastSpeakTime === "undefined" &&
      typeof p2.lastSpeakTime === "undefined"
    ) {
      return 0
    } else if (
      typeof p1.lastSpeakTime !== "undefined" &&
      typeof p2.lastSpeakTime === "undefined"
    ) {
      return -1
    } else if (
      typeof p1.lastSpeakTime === "undefined" &&
      typeof p2.lastSpeakTime !== "undefined"
    ) {
      return 1
    } else if (p1.lastSpeakTime > p2.lastSpeakTime) {
      return -1
    } else if (p1.lastSpeakTime < p2.lastSpeakTime) {
      return 1
    } else {
      return 0
    }
  } else {
    return 0
  }
}

export const sortUsersByLastSpeakTime = (p1: UserModel, p2: UserModel) => {
  if (
    typeof p1.lastSpeakTime === "undefined" &&
    typeof p2.lastSpeakTime === "undefined"
  ) {
    return 0
  } else if (
    typeof p1.lastSpeakTime !== "undefined" &&
    typeof p2.lastSpeakTime === "undefined"
  ) {
    return -1
  } else if (
    typeof p1.lastSpeakTime === "undefined" &&
    typeof p2.lastSpeakTime !== "undefined"
  ) {
    return 1
  } else if (p1.lastSpeakTime > p2.lastSpeakTime) {
    return -1
  } else if (p1.lastSpeakTime < p2.lastSpeakTime) {
    return 1
  } else {
    return 0
  }
}

export const hasPermission = (value: number, permission) => {
  return (value & permission) !== 0
}

export const addBitwisePermission = (prevValue: number, permission: number) => {
  return prevValue | permission
}

export const removeBitwisePermission = (
  prevValue: number,
  permission: number
) => {
  return prevValue & ~permission
}

export const getUpdatedPermission = (
  prevPermission: number,
  permission?: number,
  addPermissions?: string | number,
  removePermissions?: string | number
) => {
  if (permission) return permission
  let newPermissions = prevPermission

  if (removePermissions) {
    newPermissions = removeBitwisePermission(newPermissions, +removePermissions)
  }

  if (addPermissions) {
    newPermissions = addBitwisePermission(newPermissions, +addPermissions)
  }
  return newPermissions
}

export const chatMessagesSorter = (c1: ChatModel, c2: ChatModel) => {
  // if(c1.timestamp === "undefined" || c2.timestamp === "undefined"){
  //   return 0
  // }
  if (c1.timestamp < c2.timestamp) {
    return -1
  } else {
    return 1
  }
}

export const chatReplyMessagesSorter = (c1: ChatModel, c2: ChatModel) => {
  if (c1.timestamp < c2.timestamp) {
    return -1
  } else {
    return 1
  }
}

export const setPageAttributes = (pageTitle = null, pageFavicon = "") => {
  //Page Title
  if (pageTitle) {
    document.title = pageTitle
  }

  //Page Favicon
  let link: LinkElement = document.querySelector("link[rel~='icon']")
  if (!link) {
    link = document.createElement("link")
    link.rel = "icon"
    document.getElementsByTagName("head")[0].appendChild(link)
  }
  link.href = pageFavicon
}

export const containsEmoji = (string: string) => {
  const emojiRegex =
    /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/gi

  return emojiRegex.test(string)
}

export const findThePercentage = (sum, number) => {
  const result = (number * 100) / sum
  if (isNaN(result)) {
    return 0
  }

  return result
}

export const urlIfy = (text: string) => {
  var urlRegex =
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/gim
  return text.replace(urlRegex, function (url) {
    let hrefLink = url.replace(/(^\w+:|^)\/\//, "")
    hrefLink = "https://" + hrefLink
    return (
      '<a style="text-decoration: underline; color: #2c8ebb" target="_blank" href="' +
      hrefLink +
      '">' +
      url +
      "</a>"
    )
  })
}

export const scrollToBottom = id => {
  const element = document.getElementById(id)

  element.scrollTop = element.scrollHeight
}

export const createTamplatePoll = () => {
  const pollId = getRandomIntInclusive(1, 100)

  return {
    id: `pollId-${pollId}-${uuidv4()}`,
    title: "",
    isVisible: false,
    totalResponses: 0,
    isDefault: true,
    timestamp: "",
    choices: [
      {
        choice: "",
        vote: 0
      },
      { choice: "", vote: 0 }
    ],
    author: {
      id: "",
      name: ""
    },
    isVisibleMobile: true
  }
}

export const emodjiList = [
  ["glass", "\u{1F60E}"],
  ["fire", "\u{1F525}"],
  ["heart", "\u{2764}"],
  ["smile", "\u{1F600}"],
  ["bigSmile", "\u{1F923}"],
  ["loveEye", "\u{1F60D}"],
  ["check", "\u{2705}"],
  ["ok", "\u{1F44D}"]
]

export const convertToUnicode = (name: string, list: string[][]) => {
  return list.filter(item => name === item[0])[0][1]
}

export const hasVerticalScrollbar = id => {
  const div = document.getElementById(id)

  return div?.scrollHeight > div?.clientHeight
}

export const numberWithCommas = x => {
  var parts = x.toString().split(".")
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  return parts.join(".")
}

export const numberWithoutCommas = x => {
  return x.replace(/,/g, "")
}

export const parseTargetUser = (data: string) => {
  const user = {}
  data.split(", ").forEach(item => {
    if (item.startsWith("_name")) {
      user[`targetName`] = item.slice(6)
    }
  })
  return user
}

export const wasAllowedBefore = (oldPermissions: number, p: number) => {
  return (oldPermissions & p) !== 0
}
export const isAllowedNow = (currentPermissions: number, p: number) => {
  return (currentPermissions & p) !== 0
}

export const checkIfPermissionUpdate = (
  newPermissions: number,
  oldPermissions: number,
  permissionToCheck: number
) => {
  return (
    isAllowedNow(newPermissions, permissionToCheck) !==
    wasAllowedBefore(oldPermissions, permissionToCheck)
  )
}

export const isRGBAcolor = (color: string): boolean => color?.startsWith("rgba")

export const addHexColorOpacity = (color: string, opacity: number) => {
  const opacityValues = {
    1: "FF",
    0.95: "F2",
    0.9: "E6",
    0.85: "D9",
    0.8: "CC",
    0.75: "BF",
    0.7: "B3",
    0.65: "A6",
    0.6: "99",
    0.55: "8C",
    0.5: "80",
    0.45: "73",
    0.4: "66",
    0.35: "59",
    0.3: "4D",
    0.25: "40",
    0.2: "33",
    0.15: "26",
    0.1: "1A",
    0.05: "0D",
    0.0: "00"
  }

  return `${color}${opacityValues[opacity]}`
}

export const removeOpacity = (color: string): string =>
  color.replace(/[^,]+(?=\))/, "1")

export const replaceOpacity = (color: string, newOpacity: number): string => {
  return color.replace(/[^,]+(?=\))/, `${newOpacity}`)
}

export const getOpacity = (color: string) => {
  const splitArray = color.split(",")
  return +splitArray[splitArray.length - 1].slice(0, -1)
}

export const blendWithWhiteBackground = (color: string) => {
  if (!color) return undefined

  try {
    if (!color.startsWith("rgba")) {
      return color
    }
    const rgbaValues = color
      .match(/rgba?\(([\d\s.,]+)\)/)?.[1]
      .split(",")
      .map(parseFloat)
    const alpha = rgbaValues?.[3]

    if (!alpha) return color

    if (alpha === 1) {
      // No alpha channel present, return the original color
      return color
    }

    const [r, g, b] = rgbaValues.slice(0, 3)
    const blendedR = Math.round((1 - alpha) * 255 + alpha * r)
    const blendedG = Math.round((1 - alpha) * 255 + alpha * g)
    const blendedB = Math.round((1 - alpha) * 255 + alpha * b)
    const newColor = `rgb(${blendedR}, ${blendedG}, ${blendedB})`
    return newColor
  } catch (error) {
    console.error(`Error occurred while blending color ${color}:`, error)
    // Return the original color in case of an error
    return color
  }
}

export const showUserAvatar = ({
  userId,
  isWatchParty,
  isAudioWatchParty,
  isCamEnabled
}: {
  userId: string
  isWatchParty: boolean
  isAudioWatchParty: boolean
  isCamEnabled: boolean
}) => {
  let showAvatar = true
  if (
    (isWatchParty === true && !isAudioWatchParty) ||
    userId === "" ||
    isCamEnabled === true ||
    userId?.substring(0, 2) === "ss" ||
    userId?.substring(0, 2) === "wp"
  ) {
    showAvatar = false
  }
  if (isAudioWatchParty === true && isCamEnabled !== true) {
    showAvatar = true
  }
  return showAvatar
}

export const convertPoll = (data: string) => {
  const object = data.split(",").reduce((acc, el) => {
    const arrayOption = el.split("###")
    acc[arrayOption[0]] = arrayOption[1]

    return acc
  }, {})

  return Object.entries(object).map(el => ({
    choice: el[0],
    vote: el[1]
  }))
}

export const removeUnnecessaryReactions = (
  reactions: ReactionModalType
): ReactionModalType => {
  const object = { ...reactions }
  for (const property in object) {
    if (+object[property] <= 0) {
      delete object[property]
    }
  }
  return object
}

export const handleDisableControls = async containerId => {
  const container = (await waitForElm(containerId)) as HTMLDivElement
  const upSvgElement = (await waitForElm("fs-up-icon")) as HTMLElement
  const downSvgElement = (await waitForElm("fs-down-icon")) as HTMLElement
  if (downSvgElement) {
    if (
      container?.scrollTop >=
      container?.scrollHeight - container?.clientHeight - 10
    ) {
      downSvgElement.style.opacity = "0.5"
    } else {
      downSvgElement.style.opacity = "1"
    }
  }

  if (upSvgElement) {
    if (container?.scrollTop < 10) {
      upSvgElement.style.opacity = "0.5"
    } else {
      upSvgElement.style.opacity = "1"
    }
  }
}

export const cleanProductDescription = (description: string) => {
  return description?.replaceAll("###", "")
}

export const stripHtmlTags = (str: string) => {
  str = str?.replace(/<ul>[\s\S]*?<\/ul>/gi, "")

  str = str?.replace(/<ol>[\s\S]*?<\/ol>/gi, "")

  str = str?.replace(/<[^>]+>/g, "")
  return str
}

export const urlBarNarrowed = () => {
  const screenWidth = window.screen.width
  const screenHeight = window.screen.height
  const windowWidth = window.innerWidth
  const windowHeight = window.innerHeight
  const isPortrait = screenHeight > screenWidth
  const sizeDifference = isPortrait
    ? screenHeight - windowHeight
    : screenWidth - windowWidth

  return !(sizeDifference > 100)
}

export const isButtonHidden = (userPermissions: number, p: number) => {
  return (userPermissions & p) === 0
}

export const isFullScreenActive = () => !!window.document.fullscreenElement

export const wrapLinksInAnchorTags = (str, target = "_blank") => {
  // wrap the link with <a> and target if not present
  try {
    // wrap link with url if <a> tag
    const urlRegex = /\bhttps?:\/\/\S+\b/gi

    if (!/<a\b/.test(str)) {
      return str.replace(urlRegex, url => {
        // If a URL is found and not wrapped in <a> tag
        return `<a href="${url}" target="${target}">${url}</a>`
      })
    }
  } catch (error) {
    console.error("Error in wrapLinksInAnchorTags: ", error)
  }

  return str
}

export const exitFullScreen = async () => {
  try {
    // check full screen is active
    if (window.document.fullscreenElement) {
      if (document.exitFullscreen) {
        await document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen()
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen()
      }
    }
  } catch (error) {
    // can ignore error
  }
}

/**
 * return true if picture in picture is active else false
 */
export const isPictureInPictureActive = () => !!document.pictureInPictureElement

export const checkIsTouchDevice = () => {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  )
}

export const openLinkOnNewTab = (url: string) => window.open(url, "_blank")

/** This method will add the new item and update the item if that item is already present in originalList */
export function addOrUpdateList<T>(
  originalList: T[],
  newList: T[],
  compareUnique: (item1: T, item2: T) => boolean
): T[] {
  const updatedList: T[] = []

  // Update existing items in the originalList
  originalList.forEach(existingItem => {
    const matchingNewItem = newList.find(newItem =>
      compareUnique(existingItem, newItem)
    )

    if (matchingNewItem) {
      updatedList.push({ ...existingItem, ...matchingNewItem })
    } else {
      updatedList.push(existingItem)
    }
  })

  // Add new items to the updatedList
  newList.forEach(newItem => {
    if (
      !originalList.some(existingItem => compareUnique(existingItem, newItem))
    ) {
      updatedList.push(newItem)
    }
  })

  return updatedList
}

// Return unique value
export function getUniqueList(array: any[]) {
  return Array.from(new Set(array))
}

// convert the value into pixel
export const toPixelString = (value: string | number) => {
  return value ? `${value}px` : null
}

// check whether given watch party id is MNP
export const checkIsModNotPresentId = (watchPartyHostId: string) => {
  return Object.values(ModNotPresentType).includes(watchPartyHostId as any)
}

/** This function append the current page query params to the given routes */
export const addCurrentQueryParams = (route: string) => {
  return route + MOLocation.getLocation().search
}

export function parseLocalStorage<ItemType>(
  key: string,
  fallback: ItemType
): ItemType {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : fallback
  } catch {
    return fallback
  }
}
